import { createGlobalStyle } from "styled-components";

export const GlobalStyle = createGlobalStyle`
  /* ─────────────────────────────────────────────────────────
   * DESIGN TOKENS — Catppuccin Macchiato palette
   * Developer-native color system, not generic SaaS purple
   * ───────────────────────────────────────────────────────── */
  :root {
    /* Fonts — three distinct voices */
    --font-heading: 'Syne', 'Space Grotesk', sans-serif;
    --font-body: 'Inter', 'DM Sans', sans-serif;
    --font-code: 'JetBrains Mono', 'Fira Code', monospace;

    /* Dark mode — Catppuccin Macchiato */
    --bg-base:     #1e1e2e;   /* Deep developer dark — not cold, not harsh */
    --bg-surface:  #24273a;   /* Card backgrounds */
    --bg-overlay:  #2a2d3e;   /* Raised elements, inputs */
    --bg-border:   rgba(198, 160, 246, 0.12); /* Subtle mauve borders */

    --accent-mauve:  #c6a0f6;   /* Primary — name, section headings, active nav */
    --accent-teal:   #8bd5ca;   /* Secondary — links, tags, CTAs */
    --accent-peach:  #f5a97f;   /* Tertiary — hover states, timeline dots, emphasis */
    --accent-green:  #a6da95;   /* Status online / open-to-work */
    --accent-blue:   #91d7e3;   /* Subtle info / secondary links */
    --accent-red:    #ed8796;   /* Error states, disabled-link on hover */

    --text-primary:  #cad3f5;   /* Warm light — not harsh white */
    --text-muted:    #6e738d;   /* IDE comment color — captions, dates, metadata */
    --text-faint:    #494d64;   /* Very subtle — placeholder-level text */

    /* Legacy aliases (keep for components that still reference these) */
    --pink:   var(--accent-teal);
    --green:  var(--accent-mauve);
    --blue:   var(--accent-blue);
    --black:  var(--bg-base);
    --purple: var(--accent-mauve);

    scroll-padding-top: 10rem;

    /* ── Light mode overrides ── */
    &.light {
      --bg-base:     #eff1f5;   /* Catppuccin Latte — warm linen */
      --bg-surface:  #e6e9ef;
      --bg-overlay:  #dce0e8;
      --bg-border:   rgba(136, 57, 239, 0.12);

      --accent-mauve:  #8839ef;
      --accent-teal:   #179299;
      --accent-peach:  #fe640b;
      --accent-green:  #40a02b;
      --accent-blue:   #04a5e5;
      --accent-red:    #d20f39;

      --text-primary:  #4c4f69;
      --text-muted:    #9ca0b0;
      --text-faint:    #bcc0cc;

      --pink:   var(--accent-teal);
      --green:  var(--accent-mauve);
      --blue:   var(--accent-blue);
      --black:  var(--bg-base);

      body {
        transition: background-color 0.4s ease, color 0.4s ease;
        background-color: var(--bg-base);
        color: var(--text-primary);
      }

      .logo { color: var(--text-primary); }

      header.header-fixed {
        transition: 0.4s;
        background: rgba(239, 241, 245, 0.85);
        -webkit-backdrop-filter: blur(20px) saturate(160%);
        backdrop-filter: blur(20px) saturate(160%);
        border-bottom: 1px solid var(--bg-border);
        box-shadow: 0 4px 24px rgba(76, 79, 105, 0.08);

        a {
          color: var(--text-primary);

          &:hover {
            background: rgba(136, 57, 239, 0.07);
            color: var(--accent-mauve);
          }

          &.nav-active {
            color: var(--accent-mauve);
            background: rgba(136, 57, 239, 0.08);
            box-shadow: inset 0 0 0 1px rgba(136, 57, 239, 0.2);
          }

          &.button {
            color: #fff !important;
            background: linear-gradient(135deg, var(--accent-mauve) 0%, var(--accent-teal) 100%);
            box-shadow: 0 4px 16px rgba(136, 57, 239, 0.3);

            &:hover {
              opacity: 0.9;
              color: #fff !important;
            }
          }
        }

        .theme-btn {
          background: rgba(136, 57, 239, 0.06);
          border-color: rgba(136, 57, 239, 0.2);
          color: var(--text-primary);

          &:hover {
            background: rgba(136, 57, 239, 0.12);
            border-color: var(--accent-mauve);
            color: var(--accent-mauve);
          }
        }

        .menu, .menu:before, .menu:after {
          background-color: var(--text-primary);
        }
        .menu.active { background-color: transparent; }

        @media (max-width: 960px) {
          nav {
            background: rgba(239, 241, 245, 0.97);
            -webkit-backdrop-filter: blur(24px);
            backdrop-filter: blur(24px);
          }
        }
      }

      footer.footer {
        transition: 0.4s;
        background-color: var(--bg-surface);
        color: var(--text-primary);
      }

      form {
        input, textarea {
          transition: 0.4s;
          border: 1.5px solid var(--bg-border);
          color: var(--text-primary);
          background: var(--bg-overlay);

          &::placeholder {
            color: var(--text-muted);
          }

          &:focus {
            border-color: var(--accent-mauve);
          }
        }
      }
    }
  }

  /* ─────────────────────────────────────────────────────────
   * GRADIENT TEXT — uses mauve/teal instead of SaaS cyan/purple
   * ───────────────────────────────────────────────────────── */
  .gradient-text {
    background: linear-gradient(
      135deg,
      var(--accent-mauve) 0%,
      var(--accent-teal) 50%,
      var(--accent-mauve) 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: gradient-flow 6s linear infinite;
  }

  @keyframes gradient-flow {
    0%   { background-position: 0% center; }
    50%  { background-position: 100% center; }
    100% { background-position: 0% center; }
  }

  /* ─────────────────────────────────────────────────────────
   * SECTION EYEBROW — Mono structural label
   * ───────────────────────────────────────────────────────── */
  .section-eyebrow {
    display: block;
    font-family: var(--font-code);
    font-size: 1.2rem;
    font-weight: 500;
    letter-spacing: 0.12em;
    color: var(--accent-teal);
    text-transform: uppercase;
    margin-bottom: 1.2rem;
    opacity: 0.85;
  }

  /* ─────────────────────────────────────────────────────────
   * SOFT GLOW — updated to mauve
   * ───────────────────────────────────────────────────────── */
  .soft-glow {
    box-shadow: 0 0 25px rgba(198, 160, 246, 0.25);
  }

  .soft-glow-accent {
    box-shadow: 0 0 25px rgba(139, 213, 202, 0.25);
  }

  /* ─────────────────────────────────────────────────────────
   * RESET
   * ───────────────────────────────────────────────────────── */
  ul, li {
    text-decoration: none;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 62.5%;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: none;

    @media (prefers-reduced-motion: no-preference) {
      scroll-behavior: smooth;
    }
  }

  body {
    font-size: 1.6rem;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: var(--bg-base);
    color: var(--text-primary);
    overscroll-behavior-y: none;
  }

  body, input, textarea, button {
    font-family: var(--font-body);
    font-weight: 400;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-heading);
    letter-spacing: -0.02em;
  }

  /* Remove 300ms tap delay */
  a, button, [role="button"], input, label, select, textarea {
    touch-action: manipulation;
  }

  a { text-decoration: none; }

  button, .button {
    border: none;
    cursor: pointer;
    background-color: var(--accent-mauve);
    color: var(--bg-base);
    border-radius: 0.8rem;
    font-weight: 600;
    transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
    white-space: nowrap;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(198, 160, 246, 0.35);
    }
  }

  button:disabled, .button:disabled {
    filter: brightness(0.6);
    cursor: not-allowed;
  }

  .logo {
    font-size: 3rem;
    color: var(--text-primary);
    font-family: var(--font-heading);
    font-weight: 700;
  }

  /* ─────────────────────────────────────────────────────────
   * ANIMATIONS
   * ───────────────────────────────────────────────────────── */
  .animate-fadeInLeft {
    opacity: 0;
    transform: translateX(-30px);
    animation: fadeInLeft 0.7s ease-out forwards;
  }

  .animate-fadeInUp {
    opacity: 0;
    transform: translateY(24px);
    animation: fadeInUp 0.7s ease-out forwards;
  }

  .animate-fadeInRight {
    opacity: 0;
    transform: translateX(30px);
    animation: fadeInRight 0.7s ease-out forwards;
  }

  .animate-fadeIn {
    opacity: 0;
    animation: fadeIn 0.7s ease-out forwards;
  }

  .animate-flipInX {
    backface-visibility: visible !important;
    animation: flipInX 0.9s both;
  }

  @keyframes flipInX {
    from {
      transform: perspective(400px) rotate3d(1, 0, 0, 90deg);
      animation-timing-function: ease-in;
      opacity: 0;
    }
    40% {
      transform: perspective(400px) rotate3d(1, 0, 0, -20deg);
      animation-timing-function: ease-in;
    }
    60% {
      transform: perspective(400px) rotate3d(1, 0, 0, 10deg);
      opacity: 1;
    }
    80% { transform: perspective(400px) rotate3d(1, 0, 0, -5deg); }
    to  { transform: perspective(400px); }
  }

  @keyframes fadeIn      { to { opacity: 1; } }
  @keyframes fadeInLeft  { to { opacity: 1; transform: translateX(0); } }
  @keyframes fadeInUp    { to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeInRight { to { opacity: 1; transform: translateX(0); } }

  /* ─────────────────────────────────────────────────────────
   * TEXT SELECTION
   * ───────────────────────────────────────────────────────── */
  ::selection {
    background-color: rgba(198, 160, 246, 0.35);
    color: var(--text-primary);
  }

  /* ─────────────────────────────────────────────────────────
   * CUSTOM SCROLLBAR
   * ───────────────────────────────────────────────────────── */
  ::-webkit-scrollbar        { width: 6px; }
  ::-webkit-scrollbar-track  { background: var(--bg-surface); }
  ::-webkit-scrollbar-thumb  {
    background: var(--accent-mauve);
    border-radius: 10px;
    opacity: 0.7;
  }
  ::-webkit-scrollbar-thumb:hover { background: var(--accent-teal); }

  html {
    scrollbar-color: var(--accent-mauve) var(--bg-surface);
    scrollbar-width: thin;
  }

  /* ─────────────────────────────────────────────────────────
   * FOCUS STATES — keyboard-only, visible, themed
   * ───────────────────────────────────────────────────────── */
  :focus-visible {
    outline: 2px solid var(--accent-mauve);
    outline-offset: 3px;
    border-radius: 4px;
  }
  :focus:not(:focus-visible) { outline: none; }

  /* ─────────────────────────────────────────────────────────
   * REDUCED MOTION
   * ───────────────────────────────────────────────────────── */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  .no-animations,
  .no-animations *,
  .no-animations *::before,
  .no-animations *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    animation-delay: 0ms !important;
    transition-delay: 0ms !important;
    filter: none !important;
    backdrop-filter: none !important;
  }

  .no-animations .animate-fadeInUp,
  .no-animations .animate-fadeInLeft,
  .no-animations .animate-fadeInRight,
  .no-animations .animate-fadeIn,
  .no-animations .animate-flipInX {
    opacity: 1 !important;
    transform: none !important;
  }

  .no-animations .gradient-text { animation: none !important; }
`;